import { useState, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import Header from '../../components/ui/Header';
import ProjectCard from './components/ProjectCard';
import ProjectModal from './components/ProjectModal';
import FilterBar from './components/FilterBar';
import EmptyState from './components/EmptyState';
import { Project, ProjectCategory, FilterState } from './types';

const PortfolioShowcase = () => {
    const { t } = useTranslation();
    const [selectedProject, setSelectedProject] = useState<Project | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [filters, setFilters] = useState<FilterState>({
        category: 'all',
        searchQuery: ''
    });

    const projects: Project[] = [
        {
            id: '1',
            title: 'VeezDash',
            category: t('portfolio.projects.veezdash.category'),
            description: t('portfolio.projects.veezdash.desc'),
            challenge: t('portfolio.projects.veezdash.challenge'),
            solution: t('portfolio.projects.veezdash.solution'),
            impact: t('portfolio.projects.veezdash.impact'),
            technologies: ['Laravel', 'Inertia', 'MySQL', 'React.js', 'TailwindCSS', 'TypeScript', 'Redux', 'Breeze', 'PDFdom', 'Claude API'],
            image: '/screenshots/veezdash/main.png',
            alt: 'VeezDash dashboard interface',
            liveUrl: 'https://veezdash.com',
            featured: true,
            screenshots: [
                '/screenshots/veezdash/main.png',
                '/screenshots/veezdash/veez-dash (2).png',
                '/screenshots/veezdash/veez-dash (3).png',
                '/screenshots/veezdash/veez-dash (4).png',
                '/screenshots/veezdash/veez-dash (5).png',
                '/screenshots/veezdash/veez-dash (6).png',
                '/screenshots/veezdash/veez-dash (7).png',
                '/screenshots/veezdash/veez-dash (8).png',
                '/screenshots/veezdash/veez-dash (9).png',
                '/screenshots/veezdash/veez-dash (10).png',
                '/screenshots/veezdash/veez-dash (11).png',
                '/screenshots/veezdash/veez-dash (12).png',
                '/screenshots/veezdash/veez-dash (13).png',
                '/screenshots/veezdash/veez-dash (14).png',
                '/screenshots/veezdash/veez-dash (15).png',
            ]
        },
        {
            id: '2',
            title: 'Boitr',
            category: t('portfolio.projects.boitr.category'),
            description: t('portfolio.projects.boitr.desc'),
            challenge: t('portfolio.projects.boitr.challenge'),
            solution: t('portfolio.projects.boitr.solution'),
            impact: t('portfolio.projects.boitr.impact'),
            technologies: ['React.js', 'Next.js', 'TailwindCSS', 'TypeScript', 'Redux'],
            image: '/screenshots/boitr/main.png',
            alt: 'Boitr interface',
            liveUrl: 'https://boitr.com',
            featured: true,
            screenshots: [
                '/screenshots/boitr/main.png',
                '/screenshots/boitr/boitr (1).png',
                '/screenshots/boitr/boitr (2).png',
                '/screenshots/boitr/boitr (3).png',
                '/screenshots/boitr/boitr (4).png',
                '/screenshots/boitr/boitr (5).png',
                '/screenshots/boitr/boitr (6).png',
                '/screenshots/boitr/boitr (7).png',
                '/screenshots/boitr/boitr (8).png',
                '/screenshots/boitr/boitr (9).png',
                '/screenshots/boitr/boitr (10).png',
                '/screenshots/boitr/boitr (11).png',
                '/screenshots/boitr/boitr (12).png',
                '/screenshots/boitr/boitr (13).png',
                '/screenshots/boitr/boitr (14).png',
                '/screenshots/boitr/boitr (15).png',
                '/screenshots/boitr/boitr (16).png',
                '/screenshots/boitr/boitr (17).png',
                '/screenshots/boitr/boitr (18).png',
                '/screenshots/boitr/boitr (19).png',
                '/screenshots/boitr/boitr (20).png',
                '/screenshots/boitr/boitr (21).png',
                '/screenshots/boitr/boitr (22).png',
                '/screenshots/boitr/boitr (23).png',
                '/screenshots/boitr/boitr (24).png',
            ]
        },
        {
            id: '3',
            title: 'Effectra Framework',
            category: t('portfolio.projects.effectra.category'),
            description: t('portfolio.projects.effectra.desc'),
            challenge: t('portfolio.projects.effectra.challenge'),
            solution: t('portfolio.projects.effectra.solution'),
            impact: t('portfolio.projects.effectra.impact'),
            technologies: ['PHP', 'Architecture', 'Open Source', 'Routing', 'ORM'],
            image: '/screenshots/effectra/main.png',
            alt: 'Effectra Framework code and documentation',
            liveUrl: 'https://github.com/effectra',
            githubUrl: 'https://github.com/effectra',
            featured: false,
            screenshots: [
                '/screenshots/effectra/main.png',
            ]
        },{
            id:'4',
            title:'WalaaLink',
            category: t('portfolio.projects.walaalink.category'),
            description: t('portfolio.projects.walaalink.desc'),
            challenge: t('portfolio.projects.walaalink.challenge'),
            solution: t('portfolio.projects.walaalink.solution'),
            impact: t('portfolio.projects.walaalink.impact'),
            technologies: ['PHP', 'Laravel', 'React.js', 'Typescript', 'Redux.js','Rest API','IMAP','MySQL','AI','CRM','CMS','SAAS'],
            image: '/screenshots/walaalink/main.png',
            alt: 'walaalink Framework code and documentation',
            liveUrl: 'https://new.walaa.host',
            featured: false,
            screenshots: [
                '/screenshots/walaalink/main.png',
            ]
        },{
            id:'4',
            title:'AcademyRa',
            category: t('portfolio.projects.academyra.category'),
            description: t('portfolio.projects.academyra.desc'),
            challenge: t('portfolio.projects.academyra.challenge'),
            solution: t('portfolio.projects.academyra.solution'),
            impact: t('portfolio.projects.academyra.impact'),
            technologies: ['PHP', 'Laravel', 'React.js', 'Typescript', 'Redux.js','Rest API','IMAP','MySQL','AI','CRM','CMS','SAAS'],
            image: '/screenshots/academyra/main.png',
            alt: 'academyra Framework code and documentation',
            liveUrl: 'https://new.walaa.host',
            featured: false,
            screenshots: [
                '/screenshots/academyra/main.png',
                '/screenshots/academyra/academyra01.png',
                '/screenshots/academyra/academyra02.png',
                '/screenshots/academyra/academyra03.png',
                '/screenshots/academyra/academyra04.png',
                '/screenshots/academyra/academyra05.png',
                '/screenshots/academyra/academyra06.png',
                '/screenshots/academyra/academyra07.png',
                '/screenshots/academyra/academyra08.png',
                '/screenshots/academyra/academyra09.png',
                '/screenshots/academyra/academyra10.png',
                '/screenshots/academyra/academyra11.png',
                '/screenshots/academyra/academyra12.png',
                '/screenshots/academyra/academyra13.png',
                '/screenshots/academyra/academyra14.png',
                '/screenshots/academyra/academyra15.png',
                '/screenshots/academyra/academyra16.png',
                '/screenshots/academyra/academyra17.png',
                '/screenshots/academyra/academyra18.png',
            ]
        }
    ];


    const categories: ProjectCategory[] = useMemo(() => {
        const categoryMap = new Map<string, number>();
        categoryMap.set('all', projects.length);

        projects.forEach((project) => {
            const count = categoryMap.get(project.category) || 0;
            categoryMap.set(project.category, count + 1);
        });

        const cats: ProjectCategory[] = [
            { id: 'all', label: t('portfolio.allProjects'), count: projects.length }];


        categoryMap.forEach((count, category) => {
            if (category !== 'all') {
                cats.push({
                    id: category,
                    label: category,
                    count
                });
            }
        });

        return cats;
    }, [projects]);

    const filteredProjects = useMemo(() => {
        return projects.filter((project) => {
            const matchesCategory =
                filters.category === 'all' || project.category === filters.category;

            const matchesSearch =
                filters.searchQuery === '' ||
                project.title.toLowerCase().includes(filters.searchQuery.toLowerCase()) ||
                project.description.toLowerCase().includes(filters.searchQuery.toLowerCase()) ||
                project.technologies.some((tech) =>
                    tech.toLowerCase().includes(filters.searchQuery.toLowerCase())
                );

            return matchesCategory && matchesSearch;
        });
    }, [projects, filters]);

    const handleViewDetails = (project: Project) => {
        setSelectedProject(project);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setTimeout(() => setSelectedProject(null), 300);
    };

    const handleClearFilters = () => {
        setFilters({
            category: 'all',
            searchQuery: ''
        });
    };

    return (
        <>
            <Helmet>
                <title>{t('portfolio.title')} | {t('hero.name')}</title>
                <meta
                    name="description"
                    content={t('portfolio.subtitle')} />
                <meta
                    name="keywords"
                    content="portfolio, projects, full-stack development, Laravel, React, web applications, case studies" />
            </Helmet>

            <div className="min-h-screen gradient-background">
                <Header />

                <main className="pt-24 pb-20">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="text-center mb-16">
                            <div className="inline-flex items-center gap-2 px-4 py-2 glass border border-accent/30 rounded-full text-accent text-sm font-semibold mb-6">
                                <span className="w-2 h-2 bg-accent rounded-full animate-pulse" />
                                {t('portfolio.featuredWork')}
                            </div>
                            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
                                {t('portfolio.title')}
                            </h1>
                            <p className="text-lg md:text-xl text-white/65 max-w-3xl mx-auto">
                                {t('portfolio.subtitle')}
                            </p>
                        </div>

                        <FilterBar
                            categories={categories}
                            filters={filters}
                            onFilterChange={setFilters} />


                        {filteredProjects.length > 0 ?
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-12">
                                {filteredProjects.map((project) =>
                                    <ProjectCard
                                        key={project.id}
                                        project={project}
                                        onViewDetails={handleViewDetails} />

                                )}
                            </div> :

                            <EmptyState
                                searchQuery={filters.searchQuery}
                                onClearFilters={handleClearFilters} />

                        }
                    </div>
                </main>

                <ProjectModal
                    project={selectedProject}
                    isOpen={isModalOpen}
                    onClose={handleCloseModal} />

            </div>
        </>);

};

export default PortfolioShowcase;